import { patchDomFromHTML } from "../dom-patcher";
import { sharedFetchText } from "../shared/fetch-cache";
import { compileLiteral, compileHandler, parseData, CompiledTemplate } from "./template";
import { createReactiveProxy } from "./reactive";

const OBSERVED_ATTRIBUTES = [
  "data",
  "src",
  "cache",
  "cache-ttl-ms",
  "cache-max-entries",
  "timeout-ms",
  "credentials",
  "loading-class",
  "listen-events",
  "document-events",
  "listen-refresh-from",
  "lazy",
];

interface BoundHandler {
  el: Element;
  event: string;
  handler: EventListener;
}

export class DataFrameElement extends HTMLElement {
  static get observedAttributes(): string[] {
    return OBSERVED_ATTRIBUTES;
  }

  private compiled: CompiledTemplate | null = null;
  private outputEl: HTMLDivElement | null = null;
  private renderPending = false;
  private renderVersion = 0;
  private connected = false;
  private fetching = false;

  _localData: Record<string, unknown> = {};
  private _dataProxy: Record<string, unknown> | null = null;

  private fetchController: AbortController | null = null;
  private fetchVersion = 0;

  private delegatedHandlers = new Map<string, EventListener>();
  private documentHandlers = new Map<string, EventListener>();
  private broadcastHandlers = new Map<string, EventListener>();

  private inlineHandlers: BoundHandler[] = [];
  private handlerCache = new Map<string, Function>();

  private intersectionObserver: IntersectionObserver | null = null;
  private hasIntersected = false;

  /**
   * Reactive proxy over the frame's local data. Mutations auto-render.
   * Example: `el.data.count++` triggers a re-render.
   */
  get data(): Record<string, unknown> {
    if (!this._dataProxy) {
      this._dataProxy = createReactiveProxy(this._localData, () => {
        this.scheduleRender();
        this.notifyDescendants();
      });
    }
    return this._dataProxy;
  }

  set data(value: Record<string, unknown>) {
    this._localData = { ...value };
    this._dataProxy = null;
    this.scheduleRender();
    this.notifyDescendants();
  }

  connectedCallback(): void {
    this.connected = true;
    this.setAttribute("data-state", "idle");
    this.setAttribute("aria-busy", "false");
    this.seedLocalData();
    this.compileTemplate();
    this.ensureOutput();
    this.setupDelegatedListeners();
    this.setupDocumentListeners();
    this.setupBroadcastListeners();

    if (this.hasAttribute("lazy")) {
      this.setupLazyObserver();
    } else {
      this.scheduleRender();
      if (this.hasAttribute("src")) {
        this.fetchSrc();
      }
    }
  }

  disconnectedCallback(): void {
    this.connected = false;
    this.abortFetch();
    this.unbindInlineHandlers();
    this.disconnectIntersectionObserver();
    this.reconcileEventListeners(this, this.delegatedHandlers, []);
    this.reconcileEventListeners(document, this.documentHandlers, []);
    this.reconcileEventListeners(document, this.broadcastHandlers, []);
    this.outputEl = null;
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.connected) return;

    if (name === "data") {
      this.seedLocalData();
      this.scheduleRender();
      this.notifyDescendants();
      return;
    }

    if (name === "src") {
      if (newValue) {
        this.fetchSrc();
      }
      return;
    }

    if (name === "listen-events") {
      this.setupDelegatedListeners();
      return;
    }

    if (name === "document-events") {
      this.setupDocumentListeners();
      return;
    }

    if (name === "listen-refresh-from") {
      this.setupBroadcastListeners();
      return;
    }

    if (name === "lazy") {
      if (this.hasAttribute("lazy")) {
        this.setupLazyObserver();
      } else {
        this.hasIntersected = true;
        this.disconnectIntersectionObserver();
        this.scheduleRender();
        if (this.hasAttribute("src")) {
          this.fetchSrc();
        }
      }
      return;
    }

    if (name === "loading-class" && this.fetching) {
      if (oldValue) this.classList.remove(oldValue);
      const nextClass = newValue?.trim();
      if (nextClass) this.classList.add(nextClass);
      return;
    }
  }

  // -- Public API -----------------------------------------------------------

  /**
   * Returns cascaded data: all ancestor `<data-frame>` data merged
   * top-down, with this frame's local (possibly proxy-mutated) data
   * winning over ancestors.
   */
  getData(): Record<string, unknown> {
    return this.buildCascadedData();
  }

  render(): void {
    this.performRender();
  }

  // -- Internal -------------------------------------------------------------

  scheduleRender(): void {
    if (this.renderPending) return;
    this.renderPending = true;
    queueMicrotask(() => {
      this.renderPending = false;
      if (!this.connected) return;
      this.performRender();
    });
  }

  private seedLocalData(): void {
    const parsed = parseData(this);
    this._localData = { ...this._localData, ...parsed };
    this._dataProxy = null;
  }

  private abortFetch(): void {
    if (this.fetchController) {
      this.fetchController.abort();
      this.fetchController = null;
    }
  }

  private fetchSrc(cacheMode?: "default" | "reload"): void {
    const url = this.getAttribute("src")?.trim();
    if (!url) return;

    if (this.hasAttribute("lazy") && !this.hasIntersected) return;

    this.abortFetch();
    const controller = new AbortController();
    this.fetchController = controller;
    const version = ++this.fetchVersion;

    const effectiveCacheMode = cacheMode ?? (this.getAttribute("cache") as "default" | "reload" | null) ?? "default";
    const cacheTtlMs = toNumber(this.getAttribute("cache-ttl-ms"), undefined);
    const cacheMaxEntries = toNumber(this.getAttribute("cache-max-entries"), undefined);
    const timeoutMs = toNumber(this.getAttribute("timeout-ms"), undefined);
    const credentials = parseCredentials(this.getAttribute("credentials"));

    this.setFetchingState(true);
    this.dispatchEvent(
      new CustomEvent("data-frame:fetchstart", { bubbles: true, detail: { url } })
    );

    sharedFetchText(url, {
      signal: controller.signal,
      cacheMode: effectiveCacheMode,
      cacheTtlMs,
      cacheMaxEntries,
      timeoutMs,
      credentials,
    })
      .then((result) => {
        if (!this.connected || version !== this.fetchVersion) return;
        this.fetchController = null;

        let json: unknown;
        try {
          json = JSON.parse(result.bodyText);
        } catch (err) {
          this.setFetchingState(false);
          this.setAttribute("data-state", "error");
          this.dispatchEvent(
            new CustomEvent("data-frame:error", {
              bubbles: true,
              detail: { error: err, phase: "fetch-parse", url, status: result.status },
            })
          );
          return;
        }

        if (json && typeof json === "object" && !Array.isArray(json)) {
          Object.assign(this._localData, json as Record<string, unknown>);
        } else {
          this._localData._response = json;
        }
        this._dataProxy = null;

        this.setFetchingState(false);
        this.scheduleRender();
        this.notifyDescendants();

        this.dispatchEvent(
          new CustomEvent("data-frame:load", {
            bubbles: true,
            detail: {
              url,
              status: result.status,
              fromCache: result.fromCache,
            },
          })
        );
      })
      .catch((err) => {
        if (!this.connected || version !== this.fetchVersion) return;
        this.fetchController = null;

        if (err && typeof err === "object" && (err as { name?: string }).name === "AbortError") {
          this.setFetchingState(false);
          return;
        }

        this.setFetchingState(false);
        this.setAttribute("data-state", "error");
        this.dispatchEvent(
          new CustomEvent("data-frame:error", {
            bubbles: true,
            detail: { error: err, phase: "fetch", url },
          })
        );
      });
  }

  /**
   * Trigger a re-fetch of the `src` URL.
   * By default bypasses the shared fetch cache (`"reload"`).
   * Pass `{ cache: "default" }` to allow cached responses.
   */
  refresh(options?: { cache?: "default" | "reload" }): void {
    this.fetchSrc(options?.cache ?? "reload");
  }

  private buildCascadedData(): Record<string, unknown> {
    const chain: DataFrameElement[] = [];
    let cur: Element | null = this.parentElement;

    while (cur) {
      if (cur.tagName === "DATA-FRAME") {
        chain.unshift(cur as DataFrameElement);
      }
      cur = cur.parentElement;
    }

    const acc: Record<string, unknown> = {};
    for (const ancestor of chain) {
      Object.assign(acc, ancestor._localData);
    }
    Object.assign(acc, this._localData);
    return acc;
  }

  private performRender(): void {
    if (!this.compiled || !this.outputEl) return;

    const version = ++this.renderVersion;
    const data = this.buildCascadedData();
    data.$frame = this;
    data.$parent = (this.parentElement?.closest("data-frame") as DataFrameElement) ?? null;

    let html: string;
    try {
      html = this.compiled(data);
    } catch (err) {
      this.setAttribute("data-state", "error");
      this.dispatchEvent(
        new CustomEvent("data-frame:error", {
          bubbles: true,
          detail: { error: err, phase: "render" },
        })
      );
      return;
    }

    if (version !== this.renderVersion) return;

    const attrs = serializeAttributes(this.outputEl);
    patchDomFromHTML(`<div${attrs}>${html}</div>`, this.outputEl);

    this.bindInlineHandlers();
    if (!this.fetching) {
      this.setAttribute("data-state", "ready");
    }

    this.dispatchEvent(
      new CustomEvent("data-frame:render", { bubbles: true })
    );
  }

  private compileTemplate(): void {
    const tpl = this.querySelector(":scope > template");
    if (!tpl || !(tpl instanceof HTMLTemplateElement)) return;
    const source = tpl.innerHTML;
    try {
      this.compiled = compileLiteral(source);
    } catch (err) {
      this.setAttribute("data-state", "error");
      this.dispatchEvent(
        new CustomEvent("data-frame:error", {
          bubbles: true,
          detail: { error: err, phase: "compile" },
        })
      );
    }
  }

  private ensureOutput(): void {
    let output = this.querySelector(":scope > div[data-frame-output]") as HTMLDivElement | null;
    if (!output) {
      const legacyDiv = this.querySelector(":scope > div") as HTMLDivElement | null;
      if (legacyDiv) {
        legacyDiv.setAttribute("data-frame-output", "");
        if (!legacyDiv.hasAttribute("style")) {
          legacyDiv.style.display = "contents";
        }
        this.outputEl = legacyDiv;
        return;
      }

      const legacyOutput = this.querySelector(":scope > output");
      output = document.createElement("div");
      output.setAttribute("data-frame-output", "");

      if (legacyOutput) {
        for (let i = 0; i < legacyOutput.attributes.length; i++) {
          const attr = legacyOutput.attributes[i];
          output.setAttribute(attr.name, attr.value);
        }
        if (!output.hasAttribute("style")) {
          output.style.display = "contents";
        }
        while (legacyOutput.firstChild) {
          output.append(legacyOutput.firstChild);
        }
        legacyOutput.replaceWith(output);
      } else {
        output.style.display = "contents";
        this.append(output);
      }
    }
    this.outputEl = output;
  }

  // -- Loading state --------------------------------------------------------

  private setFetchingState(loading: boolean): void {
    this.fetching = loading;
    this.toggleAttribute("loading", loading);
    this.setAttribute("aria-busy", loading ? "true" : "false");
    const loadingClass = (this.getAttribute("loading-class") ?? "").trim();
    if (loadingClass) {
      this.classList.toggle(loadingClass, loading);
    }
    if (loading) {
      this.setAttribute("data-state", "loading");
    }
  }

  // -- Lazy (IntersectionObserver) ------------------------------------------

  private setupLazyObserver(): void {
    if (!this.hasAttribute("lazy") || this.hasIntersected) return;

    if (typeof IntersectionObserver === "undefined") {
      this.hasIntersected = true;
      this.scheduleRender();
      if (this.hasAttribute("src")) this.fetchSrc();
      return;
    }

    this.disconnectIntersectionObserver();
    this.intersectionObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        this.hasIntersected = true;
        this.disconnectIntersectionObserver();
        this.scheduleRender();
        if (this.hasAttribute("src")) this.fetchSrc();
        break;
      }
    });
    this.intersectionObserver.observe(this);
  }

  private disconnectIntersectionObserver(): void {
    if (!this.intersectionObserver) return;
    this.intersectionObserver.disconnect();
    this.intersectionObserver = null;
  }

  // -- Inline event handlers (data-on-*) ------------------------------------

  private unbindInlineHandlers(): void {
    for (const { el, event, handler } of this.inlineHandlers) {
      el.removeEventListener(event, handler);
    }
    this.inlineHandlers = [];
  }

  private bindInlineHandlers(): void {
    this.unbindInlineHandlers();
    if (!this.outputEl) return;

    const elements = this.outputEl.querySelectorAll("*");
    for (const el of elements) {
      const attrs = Array.from(el.attributes);
      for (const attr of attrs) {
        if (!attr.name.startsWith("data-on-")) continue;
        const eventName = attr.name.slice(8);
        const expr = attr.value;

        let handlerFn = this.handlerCache.get(expr) as ReturnType<typeof compileHandler> | undefined;
        if (!handlerFn) {
          try {
            handlerFn = compileHandler(expr);
            this.handlerCache.set(expr, handlerFn);
          } catch (err) {
            this.dispatchEvent(
              new CustomEvent("data-frame:error", {
                bubbles: true,
                detail: { error: err, phase: "handler-compile", expression: expr },
              })
            );
            continue;
          }
        }

        const frame = this;
        const compiled = handlerFn;
        const handler = ((event: Event) => {
          const parent = (frame.parentElement?.closest("data-frame") as DataFrameElement) ?? null;
          try {
            compiled(event, el, frame, parent, frame.data);
          } catch (err) {
            frame.dispatchEvent(
              new CustomEvent("data-frame:error", {
                bubbles: true,
                detail: { error: err, phase: "handler", expression: expr },
              })
            );
          }
        }) as EventListener;

        el.addEventListener(eventName, handler);
        this.inlineHandlers.push({ el, event: eventName, handler });
      }
    }
  }

  // -- Event listeners (same pattern as remote-frame) -----------------------

  private setupDelegatedListeners(): void {
    const events = splitCsv(this.getAttribute("listen-events") ?? "");
    this.reconcileEventListeners(this, this.delegatedHandlers, events, () => {
      return () => this.scheduleRender();
    });
  }

  private setupDocumentListeners(): void {
    const events = splitCsv(this.getAttribute("document-events") ?? "");
    this.reconcileEventListeners(document, this.documentHandlers, events, () => {
      return () => this.scheduleRender();
    });
  }

  private setupBroadcastListeners(): void {
    const fromIds = splitCsv(this.getAttribute("listen-refresh-from") ?? "");
    const events = fromIds.map((id) => `remote-frame-refreshed:${id}`);
    this.reconcileEventListeners(document, this.broadcastHandlers, events, () => {
      return () => this.scheduleRender();
    });
  }

  private reconcileEventListeners(
    target: EventTarget,
    registry: Map<string, EventListener>,
    desiredEvents: string[],
    createHandler?: () => EventListener
  ): void {
    const next = new Set(desiredEvents);

    for (const [eventName, handler] of registry.entries()) {
      if (next.has(eventName)) continue;
      target.removeEventListener(eventName, handler);
      registry.delete(eventName);
    }

    if (!createHandler) return;

    for (const eventName of desiredEvents) {
      if (!eventName || registry.has(eventName)) continue;
      const handler = createHandler();
      registry.set(eventName, handler);
      target.addEventListener(eventName, handler);
    }
  }

  notifyDescendants(): void {
    const children = this.querySelectorAll("data-frame");
    for (let i = 0; i < children.length; i++) {
      const child = children[i] as DataFrameElement;
      if (typeof child.scheduleRender === "function") {
        child.scheduleRender();
      }
    }
  }
}

function serializeAttributes(el: Element): string {
  let result = "";
  for (let i = 0; i < el.attributes.length; i++) {
    const attr = el.attributes[i];
    result += ` ${attr.name}="${attr.value.replace(/"/g, "&quot;")}"`;
  }
  if (!el.hasAttribute("style")) {
    result += ' style="display:contents"';
  }
  return result;
}

function splitCsv(value: string): string[] {
  return value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

function toNumber(value: string | null, fallback: number | undefined): number | undefined {
  if (value === null || value === undefined) return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function parseCredentials(value: string | null): RequestCredentials {
  if (!value) return "same-origin";
  const normalized = value.trim().toLowerCase();
  if (normalized === "include" || normalized === "omit") return normalized;
  return "same-origin";
}

export function defineDataFrame(): void {
  if (!customElements.get("data-frame")) {
    customElements.define("data-frame", DataFrameElement);
  }
}
